import { type ProjectSettings } from "@prisma/client";

export const getBusinessDescriptionData = (
  subscription: object | null | undefined,
  projectSettings: ProjectSettings | undefined,
) => {
  // ✅ SIMPLIFIED: Always use Basic Knowledge (no paid/free distinction)
  // This is the core business description that should always be included
  const basicKnowledge = projectSettings?.businessDescription || '';
  
  // Note: Extended Knowledge (documents) is now fetched separately via getKnowledgeBaseContent
  // This function only returns Basic Knowledge for backward compatibility
  return basicKnowledge;
};

//------------------------------------------------------------------------------------------------------------------------------------------------------------

// Define an enum for roles
export enum OrganizationRole {
  Admin = "org:admin",
  Member = "org:member",
  ExternalCollaborator = "org:external_collaborator",
}

// Create a constant for valid roles
const VALID_ROLES = [
  OrganizationRole.Admin,
  OrganizationRole.Member,
  OrganizationRole.ExternalCollaborator,
];

// Function to validate if a role is valid
const validateRole = (role: string): OrganizationRole => {
  if (!VALID_ROLES.includes(role as OrganizationRole)) {
    throw new Error("Role is malformed, please contact support.");
  }
  return role as OrganizationRole;
};

// Generic action guard function
const actionGuard = async (
  role: string,
  allowedRoles: OrganizationRole[],
): Promise<boolean> => {
  const validatedRole = validateRole(role);
  if (!allowedRoles.includes(validatedRole)) {
    throw new Error("You don't have permissions to perform this action");
  }
  return true;
};

// Specific action guards using the generic function
export const adminActionGuard = (role: string) =>
  actionGuard(role, [OrganizationRole.Admin]);

export const memberActionGuard = (role: string) =>
  actionGuard(role, [OrganizationRole.Admin, OrganizationRole.Member]);

export const externalCollaboratorActionGuard = (role: string) =>
  actionGuard(role, VALID_ROLES);
